import React, { FC, RefObject } from "react";
import { Box, Button, Stack, SxProps, Theme, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { InfoIcon, NavigateIcon } from "../../../../assets/icons";
import ChartActions from "../../../charts/ChartActions";

interface Props {
	title?: string;
	info?: string; // Tooltip text shown next to the title
	linkTitle?: string; // If provided, title becomes a clickable button to navigate
	element?: React.ReactNode; // Optional custom element rendered below the title
	isLoading?: boolean;
	onReload?: VoidFunction; // Reload handler (for refresh button)
	sx?: SxProps<Theme>; // Custom styling for the header container
	containerRef: RefObject<HTMLDivElement | null>; // Reference to parent container (for ChartActions)
	search?: React.ReactNode; // Optional search component rendered on the right side of the header
}

/**
 * PaperCardHeader
 * ------------------------
 * A reusable header component for PaperCard.
 * Displays a title (optionally clickable), tooltip info, reload actions, and custom content.
 */
const PaperCardHeader: FC<Props> = ({
	title,
	info,
	linkTitle,
	element,
	isLoading,
	onReload,
	sx,
	containerRef,
	search,
}) => {
	const navigate = useNavigate();

	//  Navigation handler
	const handleNavigate = () => {
		if (linkTitle) navigate(linkTitle);
	};

	//  Info tooltip
	const infoTooltip = info ? (
		<Tooltip title={info} placement="right">
			<Box display="flex">
				<InfoIcon color="info" />
			</Box>
		</Tooltip>
	) : null;

	//  Don't render header if no title
	if (!title) return null;

	return (
		<Stack direction="column" sx={sx} px={2} py={1}>
			{/*  Top Row: Title + Info + Actions  */}
			<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} minHeight={"42px"}>
				<Stack direction={"row"} spacing={1} alignItems={"center"}>
					{/* Title (plain or clickable) */}
					{!linkTitle ? (
						<Typography variant="subtitle1">{title}</Typography>
					) : (
						<Button onClick={handleNavigate} endIcon={<NavigateIcon />}>
							<Typography variant="subtitle1">{title}</Typography>
						</Button>
					)}

					{/* Info icon (if available) */}
					{infoTooltip}
				</Stack>

				{/* Optional search component */}
				{search}

				{/* Chart actions (reload, export, etc.) */}

				{onReload && (
					<ChartActions
						containerRef={containerRef}
						chartName={title}
						isLoading={isLoading}
						onReload={onReload}
					/>
				)}
			</Stack>

			{/* Optional custom element below the header */}

			{element && (
				<Stack pt={1} alignItems={"flex-start"}>
					{element}
				</Stack>
			)}
		</Stack>
	);
};

export default PaperCardHeader;
